


















import { Prop, Component, Vue } from 'vue-property-decorator'
type NumberOfPaths = number
const COLORED_ICONS: Record<string, NumberOfPaths> = {
  'amazon-color': 2,
  'paypal-color': 3,
  'discover-color': 3,
  'jcb-color': 5,
  'mastercard-color': 3,
  'unionpay-color': 5,
  'amex-color': 2,
  'dinersclub-color': 3,
  'visa-color': 3,
  cupcake: 5,
  google: 4,
  windows: 4,
}

declare global {
  interface Document {
    fonts?: any
  }
}

/**
 * For list of icons:
 *
 *  - Go to https://icomoon.io/app/#/projects
 *  - Load appropriate project (*Immaterial* or *Oricons*)
 *  - Click *Generate Font* at the bottom
 *
 * @displayName Oz Icon
 */
@Component({})
export default class OzIcon extends Vue {
  /**
   * If dir="rtl" is included, left-right arrows buttons are flipped
   * @default ltr
   */
  @Prop({ default: 'ltr' }) dir!: string

  /**
   * Name of the icon to load
   */
  @Prop({
    type: String,
    required: true,
    validator: (name) => !!name,
  })
  name!: string

  /**
   * Determines the size of the icon
   */
  @Prop({
    type: [String, Number],
    required: false,
    default: 'normal',
    validator: (value) => typeof value == 'number' || ['small', 'normal', 'large'].includes(value),
  })
  size!: string

  /**
   * Determines if icon should be hidden from screen reader (i.e. only decorative). Default is true.
   */
  @Prop({
    type: String,
    required: false,
    default: 'true',
    validator: (value) => ['true', 'false'].includes(value),
  })
  ariaHidden!: boolean

  /**
   * Determines the icon font to load
   * @values immaterial, oricon
   */
  @Prop({
    type: String,
    required: false,
    default: 'oricon',
    validator: (value) => ['immaterial', 'oricon'].includes(value),
  })
  font!: string

  isFontLoaded = false

  get isColoredIcon() {
    return Object.keys(COLORED_ICONS).includes(this.name)
  }

  // @see https://material.io/design/usability/bidirectionality.html#mirroring-elements
  get isMirroringIcon(): boolean {
    const ORICONS_MIRRORING = [
      // Arrows
      'back',
      'forward',
      'highlight',
      // Text-related
      'subscript',
      'superscript',
      'bulleted_list',
      'numbered_list',
      'clipboard',
      'comment',
      'quote',
      'enlarge',
      'dock',
      'expand',
      'padlets',
      'send_back',
      'send_front',
      // Movement
      'transfer',
      // Passage of time
      'undo',
      'redo',
    ]
    return this.dir === 'rtl' && !!this.name && this.font == 'oricon' && ORICONS_MIRRORING.includes(this.name)
  }

  get numOfPaths() {
    return COLORED_ICONS[this.name]
  }
  get iconClass(): string {
    if (this.font === 'immaterial') {
      return 'immaterial-icons'
    }
    return 'oricon'
  }

  get styles() {
    let sizeNumber = 24
    if (typeof this.size == 'number') {
      sizeNumber = this.size
    } else if (this.size === 'small') {
      sizeNumber = 18
    } else if (this.size === 'large') {
      sizeNumber = 32
    }
    if (!this.isFontLoaded) {
      return `visibility: hidden; font-size: ${sizeNumber}px; min-width:${sizeNumber}px; max-width:${sizeNumber}px; width: ${sizeNumber}px; height: ${sizeNumber}px;`
    }

    return `font-size: ${sizeNumber}px; max-width:${sizeNumber}px; width: ${sizeNumber}px; height: ${sizeNumber}px; overflow: visible;`
  }

  async mounted() {
    if (!document.fonts && document.readyState === 'complete') {
      // If FontFaceSet API is not supported, we fallback to DOM ready state
      this.isFontLoaded = true
      return
    }
    !document.fonts &&
      window.addEventListener('DOMContentLoaded', () => {
        // If FontFaceSet API is not supported, we fallback to DOM ready state
        this.isFontLoaded = true
        return
      })
    const fontName = this.font == 'immaterial' ? 'immaterial' : 'oricons'
    await document.fonts.ready
    const fontsLoaded = await document.fonts.load(`24px ${fontName}`)
    this.isFontLoaded = fontsLoaded.length > 0
  }
}
